import * as Sentry from '@sentry/react';

// Configuración de Sentry
export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'development';
  
  // Solo inicializar Sentry si tenemos DSN configurado
  if (dsn) {
    Sentry.init({
      dsn,
      environment,
      
      // Configuración de integraciones
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],

      // Configuración de performance
      tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
      
      // Configuración de replay
      replaysSessionSampleRate: environment === 'production' ? 0.1 : 1.0,
      replaysOnErrorSampleRate: 1.0,

      // Setting this option to true will send default PII data to Sentry.
      // For example, automatic IP address collection on events
      sendDefaultPii: true,

      // Configuración de filtros
      beforeSend(event) {
        // Filtrar errores conocidos que no necesitan ser reportados
        if (event.exception) {
          const error = event.exception.values?.[0];
          const errorMessage = error?.value || '';
          
          // Filtrar errores de desarrollo comunes
          if (errorMessage.includes('ResizeObserver loop limit exceeded')) {
            return null;
          }
          
          // Filtrar errores de red específicos
          if (errorMessage.includes('Network Error') && !errorMessage.includes('API')) {
            return null;
          }
        }
        
        return event;
      },

      // Configuración de contexto inicial
      initialScope: {
        tags: {
          component: 'frontend',
          environment,
        },
      },
    });
  }
};

// Tipos para mejor tipado
export interface SentryContext {
  [key: string]: Record<string, unknown>;
}

export interface SentryUser {
  id: string;
  email?: string;
  username?: string;
  [key: string]: string | number | boolean | object | null | undefined;
}

// Función helper para capturar errores manualmente
export const captureError = (error: Error, context?: SentryContext) => {
  Sentry.withScope((scope) => {
    if (context) {
      Object.keys(context).forEach((key) => {
        scope.setContext(key, context[key] as Record<string, unknown>);
      });
    }
    Sentry.captureException(error);
  });
};

// Función helper para capturar mensajes
export const captureMessage = (
  message: string, 
  level: 'info' | 'warning' | 'error' = 'info',
  context?: SentryContext
) => {
  Sentry.withScope((scope) => {
    if (context) {
      Object.keys(context).forEach((key) => {
        scope.setContext(key, context[key] as Record<string, unknown>);
      });
    }
    Sentry.captureMessage(message, level);
  });
};

// Función helper para agregar contexto de usuario
export const setUserContext = (user: SentryUser) => {
  Sentry.setUser(user);
};

// Función helper para agregar tags
export const addTag = (key: string, value: string) => {
  Sentry.setTag(key, value);
};

// Función helper para agregar contexto adicional
export const addContext = (key: string, context: SentryContext) => {
  Sentry.setContext(key, context);
};

// Función helper para agregar breadcrumb
export const addBreadcrumb = (
  message: string,
  category?: string,
  level?: 'info' | 'warning' | 'error',
  data?: SentryContext
) => {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
};
